import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { post } from '../../api';
import { useForm } from 'react-hook-form';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const userId = searchParams.get('id');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!token || !userId) {
      toast.error('Invalid reset link');
      return;
    }

    setIsLoading(true);
    try {
      const response = await post('/api/auth/reset-password', {
        userId,
        token,
        newPassword: data.password
      });

      if (response.status === 200) {
        toast.success('Password reset successfully!');
        navigate('/login');
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred while resetting password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token || !userId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Invalid Password Reset Link
        </Typography>
        <Typography>
          The password reset link is invalid or has expired. Please request a new password reset link.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/forgot-password')}
        >
          Request New Reset Link
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        maxWidth: 400,
        margin: '0 auto'
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Reset Your Password
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Please enter your new password below.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: '100%' }}
      >
        <TextField
          fullWidth
          margin="normal"
          label="New Password"
          type="password"
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters'
            }
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Confirm New Password"
          type="password"
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: value => value === password || 'Passwords do not match'
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
        </Button>
      </Box>
    </Box>
  );
};

export default ResetPassword;